import { combineSlices, createSelector } from '@reduxjs/toolkit'

import { hasTenMinutesPassed } from '../utils'

import {
  documentBatchSlice,
  selectDocumentBatchChatInteractionLoading,
  selectDocumentBatchChatResultLoadFailed,
  selectDocumentBatchChatResultLoading,
  selectDocumentBatchDocuments,
  selectDocumentBatchLoading,
} from './documentBatch'
import {
  documentSlice,
  selectDocument,
  selectDocumentFileInfoIsLoading,
  selectDocumentLoading,
  selectDocumentSummaryEditing,
  selectDocumentSummaryExecutionLoading,
  selectDocumentSummaryInteractionLoading,
  selectDocumentSummaryResult,
  selectDocumentSummaryResultLoading,
} from './document'
import { documentProspectingSlice } from './documentProspecting'
import { documentSidebarSlice } from './documentSidebar'
import { documentFilterSlice } from './documentFilter'
import { documentTypeSlice, selectDocumentTypes } from './documentTypes'

export default combineSlices(documentTypeSlice, documentProspectingSlice, documentBatchSlice, documentSlice, documentSidebarSlice, documentFilterSlice)

export const selectShouldPollDocument = createSelector(
  [selectDocumentLoading, selectDocumentSummaryResultLoading, selectDocument, selectDocumentSummaryResult],
  (documentLoading, documentSummaryLoading, document, documentSummary) => {
    if (documentLoading || documentSummaryLoading || !document || documentSummary) {
      return false
    }

    const { typeProcessing, summaryProcessing } = document

    if (summaryProcessing.finishedAt) {
      return false
    }

    if (summaryProcessing.startedAt) {
      return !hasTenMinutesPassed(summaryProcessing.startedAt)
    }

    if (typeProcessing.finishedAt) {
      return !hasTenMinutesPassed(typeProcessing.finishedAt)
    }

    if (typeProcessing.startedAt) {
      return !hasTenMinutesPassed(typeProcessing.startedAt)
    }

    return true
  },
)

export const selectShouldPollDocumentBatch = createSelector([selectDocumentBatchLoading, selectDocumentBatchDocuments], (documentBatchLoading, documents) => {
  if (documentBatchLoading || !documents.length) {
    return false
  }

  return documents.some((document) => {
    const { typeProcessing, summaryProcessing } = document

    if (summaryProcessing.finishedAt) {
      return false
    }

    if (summaryProcessing.startedAt) {
      return !hasTenMinutesPassed(summaryProcessing.startedAt)
    }

    if (typeProcessing.finishedAt) {
      return !hasTenMinutesPassed(typeProcessing.finishedAt)
    }

    if (typeProcessing.startedAt) {
      return !hasTenMinutesPassed(typeProcessing.startedAt)
    }

    return true
  })
})

export const selectShouldPollDocumentSummary = createSelector(
  [selectShouldPollDocument, selectDocument, selectDocumentSummaryResult, selectDocumentSummaryExecutionLoading],
  (shouldPollDocument, document, documentSummaryResult, documentSummaryExecutionLoading) => {
    if (shouldPollDocument || documentSummaryResult || !document || documentSummaryExecutionLoading) {
      return false
    }

    const { summaryProcessing } = document

    return !!summaryProcessing.finishedAt && !hasTenMinutesPassed(summaryProcessing.finishedAt)
  },
)

export const selectDocumentType = createSelector([selectDocument, selectDocumentTypes], (document, documentTypes) => {
  if (!document || !documentTypes.length) {
    return null
  }

  const { typeId } = document.typeProcessing
  if (!typeId) {
    return null
  }

  const documentType = documentTypes.find((documentType) => documentType.id === typeId)
  if (!documentType) {
    return null
  }

  return documentType
})

export const selectDocumentBatchNavigationDisabled = createSelector(
  [
    selectDocumentLoading,
    selectDocumentFileInfoIsLoading,
    selectDocumentSummaryResultLoading,
    selectDocumentSummaryExecutionLoading,
    selectDocumentSummaryInteractionLoading,
    selectDocumentSummaryEditing,
    selectDocumentBatchChatInteractionLoading,
  ],
  (documentLoading, fileInfoLoading, summaryResultLoading, summaryExecutionLoading, summaryInteractionLoading, summaryEditing, chatInteractionLoading) =>
    documentLoading ||
    fileInfoLoading ||
    summaryResultLoading ||
    summaryExecutionLoading ||
    summaryInteractionLoading ||
    summaryEditing ||
    chatInteractionLoading,
)

export const selectDocumentBatchChatDisabled = createSelector(
  [
    selectDocument,
    selectDocumentBatchChatResultLoading,
    selectDocumentBatchChatResultLoadFailed,
    selectDocumentBatchChatInteractionLoading,
    selectDocumentSummaryExecutionLoading,
    selectDocumentSummaryInteractionLoading,
    selectDocumentSummaryEditing,
  ],
  (document, chatResultLoading, chatResultLoadFailed, chatInteractionLoading, summaryExecutionLoading, summaryInteractionLoading, summaryEditing) =>
    !document || chatResultLoading || chatResultLoadFailed || chatInteractionLoading || summaryExecutionLoading || summaryInteractionLoading || summaryEditing,
)
